import React from 'react'
import gsap from 'gsap'
import Milestones from '../components/Milestones.jsx'
import { api } from '../lib/api.js'

const FEATURES = [
  {
    k: 'GUNPLAY',
    t: 'Tight recoil + movement',
    d: 'A Warzone-inspired feel with deliberate recoil patterns and responsive movement tuning.'
  },
  {
    k: 'BR LOOP',
    t: 'Contracts, rotations, clutch buys',
    d: 'Fast pacing built around constant decision-making: loot, plates, contracts, rotate, fight.'
  },
  {
    k: 'INFRA',
    t: 'Launcher-first delivery',
    d: 'Structured for downloadable builds, patch notes, and version metadata from day one.'
  }
]

export default function Home() {
  const heroRef = React.useRef(null)
  const [site, setSite] = React.useState(null)

  React.useEffect(() => {
    let live = true
    ;(async () => {
      try {
        const d = await api('/api/site')
        if (live) setSite(d?.site || null)
      } catch {
        // ignore
      }
    })()
    return () => { live = false }
  }, [])

  React.useEffect(() => {
    const hero = heroRef.current
    if (!hero) return

    const tl = gsap.timeline({ defaults: { ease: 'power2.out' } })
    tl.fromTo(hero.querySelector('[data-kicker]'), { y: 10, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5 })
      .fromTo(hero.querySelector('[data-title]'), { y: 12, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6 }, '-=0.2')
      .fromTo(hero.querySelector('[data-lead]'), { y: 12, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6 }, '-=0.35')
      .fromTo(hero.querySelector('[data-cta]'), { y: 10, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5 }, '-=0.35')

    const orbs = hero.querySelectorAll('[data-orb]')
    orbs.forEach((orb, i) => {
      gsap.to(orb, {
        y: i % 2 === 0 ? 22 : -18,
        x: i % 2 === 0 ? -14 : 16,
        duration: 4.5 + i,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut'
      })
    })

    return () => {
      tl.kill()
      gsap.killTweensOf(orbs)
    }
  }, [])

  const brandName = site?.brand?.name || 'DeltaReloaded'
  const sub = site?.hero?.subheadline || 'A tactical battle royale built around aggressive rotations, tight gunplay, and fast patch delivery through a dedicated launcher.'

  return (
    <div>
      <section ref={heroRef} className="relative overflow-hidden">
        <div className="absolute inset-0 noise opacity-70" />
        <div className="absolute inset-0">
          <div data-orb className="absolute -top-20 -left-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
          <div data-orb className="absolute top-20 -right-24 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
          <div data-orb className="absolute bottom-[-120px] left-1/3 h-96 w-96 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute inset-0 grid-bg opacity-[0.14]" />
        </div>

        <div className="relative mx-auto max-w-6xl px-4 py-20 md:py-28">
          <div className="max-w-3xl">
            <div data-kicker className="text-xs tracking-[0.36em] text-white/60">WARZONE-LIKE FPS IN DEVELOPMENT</div>
            <h1 data-title className="mt-4 text-4xl md:text-6xl font-semibold tracking-tight">{brandName}</h1>
            <p data-lead className="mt-5 text-base md:text-lg text-white/65">{sub}</p>

            <div data-cta className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={site?.hero?.primaryCtaHref || '/launcher'}
                className="rounded-xl border border-white/15 bg-white/10 px-5 py-3 text-sm hover:bg-white/15"
              >
                {site?.hero?.primaryCtaText || 'Download Launcher'}
              </a>
              <a
                href="#milestones"
                className="rounded-xl border border-white/15 bg-black/20 px-5 py-3 text-sm hover:bg-white/5"
              >
                {site?.hero?.secondaryCtaText || 'View Milestones'}
              </a>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {FEATURES.map((f) => (
                <div key={f.t} className="rounded-2xl border border-white/10 bg-panel/60 glow p-5">
                  <div className="text-xs tracking-[0.28em] text-white/50">{f.k}</div>
                  <div className="mt-2 text-lg font-semibold">{f.t}</div>
                  <div className="mt-2 text-white/60 text-sm">{f.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4">
        <Milestones />

        <section className="mt-10 mb-16 rounded-2xl border border-white/10 bg-panel/60 glow p-6">
          <div className="text-xs tracking-[0.32em] text-white/60">NEXT</div>
          <div className="mt-2 text-2xl font-semibold">Join testing, track the roadmap, get builds faster</div>
          <div className="mt-2 text-white/60 max-w-3xl text-sm">
            Create an account to redeem beta keys, link Discord for role verification, and unlock download channels.
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <a href="/account" className="rounded-xl border border-white/15 bg-white/10 px-5 py-3 text-sm hover:bg-white/15">
              Account / Beta Access
            </a>
            <a href="/media" className="rounded-xl border border-white/15 bg-black/20 px-5 py-3 text-sm hover:bg-white/5">
              Trailer + Media
            </a>
          </div>
        </section>
      </div>
    </div>
  )
}
